import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Typography,
  Box,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { useDispatch, useSelector } from 'react-redux';
import { updateUserRepresentatives, searchNaturalEntities } from '../UserManagement.actions';
import color from 'utils/colors';

const useStyles = makeStyles(() => ({
  dialogTitle: {
    color: color.purpleSecondary,
    fontWeight: 'bold',
    fontSize: '20px',
    padding: '24px 24px 8px 24px'
  },
  dialogContent: {
    padding: '6px 24px 24px 24px',
    marginTop: '2px',
  },
  searchInput: {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#E0E0E0',
      },
      '&:hover fieldset': {
        borderColor: color.purpleSecondary,
      },
      '&.Mui-focused fieldset': {
        borderColor: color.purpleSecondary,
      },
    },
    '& .MuiInputLabel-root': {
      color: color.darkGrey,
      '&.Mui-focused': {
        color: color.purpleSecondary,
      },
    },
  },
  addButton: {
    backgroundColor: color.purpleSecondary,
    color: color.white,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: color.purpleSecondary,
    },
  },
  tableHeader: {
    backgroundColor: color.purpleSecondary,
    '& .MuiTableCell-head': {
      color: color.white,
      fontWeight: 'bold',
    },
  },
  suggestionsList: {
    border: '1px solid #E0E0E0',
    borderRadius: '4px',
    maxHeight: '200px',
    overflowY: 'auto',
    backgroundColor: 'white',
    position: 'absolute',
    zIndex: 1000,
    width: '100%',
    marginTop: '4px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
  },
  suggestionItem: {
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
    borderBottom: '1px solid #f0f0f0',
    '&:last-child': {
      borderBottom: 'none',
    }
  }
}));

function RepresentativesModal({ open, onClose, user, representatives = [], loading = false }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { userManagement } = useSelector((state) => state);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [representativesArray, setRepresentativesArray] = useState([]);
  const [naturalEntitiesOpt, setNaturalEntitiesOpt] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [representativeToDelete, setRepresentativeToDelete] = useState(null);
  const [deletingRepresentative, setDeletingRepresentative] = useState(false);

  // Inicializar representantes existentes
  useEffect(() => {
    if (open && user) {
      // Formatear representantes existentes
      if (representatives.length > 0) {
        const formattedReps = representatives.map(rep => ({
          id: rep.entityId || rep.naturalEntityId || rep.id, // Usar entityId como ID principal
          entityId: rep.entityId || rep.naturalEntityId, // Asegurar que tenemos entityId
          naturalEntityId: rep.naturalEntityId,
          label: `${rep.name || rep.names || ''} ${rep.surnames || ''} (${rep.rut || ''})`,
          rut: rep.rut,
          names: rep.names || rep.name,
          surnames: rep.surnames || '',
          email: rep.email
        }));
        setRepresentativesArray(formattedReps);
      } else {
        setRepresentativesArray([]);
      }

      // Buscar entidades naturales disponibles
      if (user.id) {
        dispatch(searchNaturalEntities(user.id, { search: '', page: 0, limit: 20 }));
      }
    }
  }, [open, user, representatives, dispatch]);

  // Actualizar opciones cuando lleguen los resultados de búsqueda
  useEffect(() => {
    if (userManagement.searchResults?.entities) {
      setNaturalEntitiesOpt(userManagement.searchResults.entities);
    }
  }, [userManagement.searchResults]);

  const handleAutocomplete = (event, newValue) => {
    setSelectedEntity(newValue);
  };

  // Función para buscar con debounce
  const handleSearchChange = (searchValue) => {
    setSearchTerm(searchValue);
    if (user?.id) {
      dispatch(searchNaturalEntities(user.id, { search: searchValue, page: 0, limit: 20 }));
    }
  };

  const handleAddRepresentative = () => {
    if (selectedEntity && user) {
      // Agregar a la lista local
      const newArray = [...representativesArray, selectedEntity];
      
      // Convertir para el backend
      const representativesForBackend = newArray.map(rep => ({
        id: rep.entityId || rep.id
      }));
      
      // Actualizar en el backend
      dispatch(updateUserRepresentatives(user.id, representativesForBackend));
      
      // Actualizar estado local
      setRepresentativesArray(newArray);
      
      // Remover de opciones disponibles
      setNaturalEntitiesOpt(naturalEntitiesOpt.filter(opt => opt.id !== selectedEntity.id));
      setSelectedEntity(null);
    }
  };

  const handleRemoveRepresentative = (representative) => {
    setRepresentativeToDelete(representative);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteRepresentative = () => {
    if (representativeToDelete && user) {
      setDeletingRepresentative(true);
      
      // Remover de la lista local
      const newArray = representativesArray.filter(rep => 
        (rep.entityId || rep.id) !== (representativeToDelete.entityId || representativeToDelete.id)
      );
      
      // Agregar de vuelta a opciones disponibles
      const repWithEntityId = {
        ...representativeToDelete,
        entityId: representativeToDelete.entityId || representativeToDelete.id
      };
      const optionsArray = [...naturalEntitiesOpt, repWithEntityId];
      optionsArray.sort((a, b) => (a.id > b.id ? 1 : -1));
      
      // Convertir el nuevo array para el backend
      const representativesForBackend = newArray.map(rep => ({
        id: rep.entityId || rep.id
      }));
      
      // Actualizar en el backend
      dispatch(updateUserRepresentatives(user.id, representativesForBackend));
      
      // Actualizar estado local
      setRepresentativesArray(newArray);
      setNaturalEntitiesOpt(optionsArray);
      
      // Cerrar modal
      setDeletingRepresentative(false);
      setDeleteConfirmOpen(false);
      setRepresentativeToDelete(null);
    }
  };

  const cancelDeleteRepresentative = () => {
    setDeleteConfirmOpen(false);
    setRepresentativeToDelete(null);
  };


  if (!user) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="body"
      PaperProps={{
        sx: {
          borderRadius: '8px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle className={classes.dialogTitle}>
        Listado de representantes
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
        <Box sx={{ height: '16px' }} />
        
        {/* Buscador de usuarios naturales */}
        <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={9}>
            <Autocomplete
              fullWidth
              value={selectedEntity}
              onChange={handleAutocomplete}
              options={naturalEntitiesOpt}
              loading={userManagement.loading}
              onInputChange={(event, newInputValue) => {
                handleSearchChange(newInputValue);
              }}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Buscar representante" 
                  placeholder="Escriba el nombre, apellido o RUT"
                  className={classes.searchInput}
                  InputLabelProps={{ shrink: true }}
                />
              )}
              noOptionsText="No se encontraron resultados"
              loadingText="Buscando..."
            />
          </Grid>
          <Grid item xs={3}>
            <Button
              onClick={handleAddRepresentative}
              variant="contained"
              className={classes.addButton}
              disabled={!selectedEntity}
              startIcon={<AddIcon />}
              fullWidth
            >
              Agregar
            </Button>
          </Grid>
        </Grid>

        {/* Tabla de representantes */}
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead className={classes.tableHeader}>
              <TableRow>
                <TableCell>Representantes</TableCell>
                <TableCell align="center">Eliminar</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                    <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                      <CircularProgress size={24} sx={{ color: color.purpleSecondary }} />
                      <Typography variant="body2" color="textSecondary">
                        Cargando representantes...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : representativesArray.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align="center">
                    <Typography variant="body2" color="textSecondary">
                      No hay representantes asociados
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                representativesArray.map((representative, index) => (
                  <TableRow key={representative.id || index}>
                    <TableCell>
                      <Typography variant="body2">
                        {representative.label || `${representative.names || ''} ${representative.surnames || ''}`.trim()}
                      </Typography>
                      {representative.rut && (
                        <Typography variant="caption" color="textSecondary">
                          RUT: {representative.rut}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        sx={{ color: color.purpleSecondary }}
                        onClick={() => handleRemoveRepresentative(representative)}
                        disabled={deletingRepresentative}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      
      <DialogActions sx={{ padding: '16px 24px' }}>
        <Button
          onClick={onClose}
          sx={{
            backgroundColor: color.purpleSecondary,
            color: color.white,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: color.purpleSecondary,
            },
          }}
          variant="contained"
        >
          Cerrar
        </Button>
      </DialogActions>

      {/* Modal de confirmación para eliminar */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={cancelDeleteRepresentative}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px'
          }
        }}
      >
        <DialogTitle sx={{ color: color.purpleSecondary, fontWeight: 'bold' }}>
          Confirmar eliminación
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ py: 2 }}>
            ¿Está seguro que desea eliminar al representante <strong>{representativeToDelete?.label || `${representativeToDelete?.names || ''} ${representativeToDelete?.surnames || ''}`.trim()}</strong>?
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Esta acción no se puede deshacer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={cancelDeleteRepresentative}
            sx={{
              color: color.purpleSecondary,
              borderColor: color.purpleSecondary,
              textTransform: 'none',
              '&:hover': {
                borderColor: color.purpleSecondary,
                backgroundColor: 'rgba(149, 117, 205, 0.04)',
              },
            }}
            variant="outlined"
            disabled={deletingRepresentative}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmDeleteRepresentative}
            sx={{
              backgroundColor: color.purpleSecondary,
              color: color.white,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: color.purpleSecondary,
              },
            }}
            variant="contained"
            disabled={deletingRepresentative}
          >
            {deletingRepresentative ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={16} sx={{ color: color.white }} />
                Eliminando...
              </Box>
            ) : (
              'Eliminar'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

export default RepresentativesModal;